import React, { useState, useEffect, useRef } from "react";
import "./DistrictWiseForm.css";
import { Search, ChevronDown, Check } from "lucide-react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { getData, postDataWithBody } from "../../../../Services/Services";
import {
  findRegionsByUser,
  getdistricwisedataByRole,
  getThemeURL,
} from "../../../../Services/EndPoints";
import { toast } from "react-toastify";

// Component function for displaying district wise ATM data
const DistrictWiseForm = () => {
  const [districtWiseBranches, setDistrictWiseBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loader, setLoader] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [atmData, setAtmData] = useState([]);
  const [selectedAtm, setSelectedAtm] = useState([]);
  const [expandedDistrict, setExpandedDistrict] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [districtData, setDistrictData] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  //useEffect for initial user profile and district data loading
  useEffect(() => {
    getUserProfile();
    getDistrictData();

    // Click outside handler to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    // Window resize listener for responsive layout
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Apply filtering whenever selectedDistricts or searchTerm changes
    applyFilters();
  }, [selectedDistricts, selectedAtm, searchTerm, districtWiseBranches]);

  // Extract branch data from the response
  const extractAtmData = (districtWiseAtmsData) => {
    const atms = [];
    districtWiseAtmsData.forEach((district) => {
      district.atmDetails.forEach((atm) => {
        atms.push({
          id: atm.id,
          value: atm.name,
          districtName: district.districtName,
        });
      });
    });
    setAtmData(atms);
  };

  // Check if user role requires branch dropdown
  const shouldShowAtmDropdown = () => {
    return (
      userProfile &&
      (userProfile[0]?.is_admin === true || userProfile[0]?.is_admin === false)
    );
  };

  // Enhanced global search function
  const performGlobalSearch = (data, searchTerm) => {
    if (!searchTerm.trim()) return data;

    const searchLower = searchTerm.toLowerCase().trim();

    return data
      .map((district) => {
        // Check if district name matches
        const districtMatches =
          district.districtName &&
          district.districtName.toLowerCase().includes(searchLower);

        // Filter branches that match any field
        const matchingAtms = district.atmDetails.filter((atm) => {
          // Search in all branch fields
          const atmNameMatches =
            atm.name && atm.name.toLowerCase().includes(searchLower);

          const codeMatches =
            atm.code && atm.code.toLowerCase().includes(searchLower);

          const addressMatches =
            atm.addressLine &&
            atm.addressLine.toLowerCase().includes(searchLower);

          // Return true if any field matches
          return atmNameMatches || codeMatches || addressMatches;
        });

        // If district name matches, include all branches
        // Otherwise, only include matching branches
        return {
          ...district,
          atmDetails: districtMatches ? district.atmDetails : matchingAtms,
        };
      })
      .filter((district) => district.atmDetails.length > 0);
  };

  // New function to apply both district and search filters
  const applyFilters = () => {
    if (!districtWiseBranches || districtWiseBranches.length === 0) return;

    let filtered = JSON.parse(JSON.stringify(districtWiseBranches)); // Deep copy to avoid mutation

    // Apply district filter for non-HO/U roles
    if (!shouldShowAtmDropdown() && selectedDistricts.length > 0) {
      filtered = filtered.filter((district) =>
        selectedDistricts.some(
          (selected) =>
            district.districtName.toLowerCase() === selected.toLowerCase()
        )
      );
    }

    // Apply branch filter for HO/U roles
    if (shouldShowAtmDropdown() && selectedAtm.length > 0) {
      console.log(
        "Applying branch filter with selected branches:",
        selectedAtm
      );

      filtered = filtered
        .map((district) => {
          const filteredBranches = district.atmDetails.filter((atm) => {
            const isMatched = selectedAtm.some(
              (selected) =>
                atm.name.toLowerCase().trim() === selected.toLowerCase().trim()
            );
            console.log(`Branch ${atm.name} matched:`, isMatched);
            return isMatched;
          });

          return {
            ...district,
            atmDetails: filteredBranches,
          };
        })
        .filter((district) => district.atmDetails.length > 0);

      console.log("Filtered result after branch filter:", filtered);
    }

    // Apply enhanced global search filter
    if (searchTerm.trim() !== "") {
      filtered = performGlobalSearch(filtered, searchTerm);
    }

    console.log("Final filtered branches:", filtered);
    setFilteredBranches(filtered);

    if (
      filtered.length === 1 &&
      (selectedDistricts.length > 0 || selectedAtm.length > 0)
    ) {
      setExpandedDistrict(filtered[0].districtName);
    }
  };
  // Get user profile details Api Call
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      setUserProfile(userProfile);
      if (userProfile && userProfile.length > 0) {
        getAllDistrictsData(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const createPayload = (profile) => {
    let admin_role;
 
    if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    } else if (profile.is_admin === true) {
      admin_role = "HO";
    } else {
      admin_role = "U";
    }
 
    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  // Get all district wise ATM data Api Call
  const getAllDistrictsData = async (profile) => {
    setLoader(true);
    const payload = createPayload(profile);
    var allDistrictsData = await postDataWithBody(
      getdistricwisedataByRole,
      payload,
      {}
    );
    setLoader(false);
    setDistrictWiseBranches(allDistrictsData);
    setFilteredBranches(allDistrictsData);

    // Extract branch data for HO/U roles
    extractAtmData(allDistrictsData);

    sessionStorage.setItem(
      "districtWiseATMs",
      JSON.stringify(allDistrictsData)
    );
    console.log("allDistrictsData", allDistrictsData);
  };

  const getDistrictData = async () => {
    try {
      const distList = await getData(findRegionsByUser);

      if (Array.isArray(distList) && distList.length > 0) {
        setDistrictData(distList);
      } else {
        console.error("Error: District data is empty or not an array");
      }
    } catch (error) {
      console.error("Error fetching district list:", error);
    }
  };

  const toggleExpand = (districtName) => {
    setExpandedDistrict(
      expandedDistrict === districtName ? null : districtName
    );
  };

  const getPastelColor = (count) => {
    return "#00C11433";
  };
  //District dropdown handler
  const handleDistrictSelect = (district) => {
    setSelectedDistricts((prev) => {
      // If already selected, remove it
      if (prev.includes(district)) {
        return prev.filter((d) => d !== district);
      }
      // Otherwise, add it
      return [...prev, district];
    });
    // Keep dropdown open for multiple selections
  };
  //Atm dropdown handler
  const handleAtmSelect = (atm) => {
    setSelectedAtm((prev) => {
      const newSelection = prev.includes(atm)
        ? prev.filter((b) => b !== atm)
        : [...prev, atm];

      // Trigger filtering immediately after state update
      setTimeout(() => {
        applyFilters();
      }, 0);

      return newSelection;
    });
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const clearAllSelections = () => {
    setSelectedDistricts([]);
  };

  const clearAllAtmSelections = () => {
    setSelectedAtm([]);
  };

  // Determine number of columns based on screen width
  const getColumnCount = () => {
    if (windowWidth < 768) return 1;
    if (windowWidth < 992) return 2;
    return 3;
  };

  // Split districts into columns dynamically based on screen size
  const splitIntoColumns = (data) => {
    if (!data || data.length === 0) return [[]];

    const columnCount = getColumnCount();
    const result = Array(columnCount)
      .fill()
      .map(() => []);

    data.forEach((item, index) => {
      const columnIndex = index % columnCount;
      result[columnIndex].push(item);
    });

    return result;
  };

  const columns = splitIntoColumns(filteredBranches);

  // Function to render a district card
  const renderDistrictCard = (district, index) => {
    if (!district || !district.atmDetails) return null;

    const activeBranches = district.atmDetails;
    const branchCount = activeBranches.length;
    const shouldScroll = branchCount > 5;

    // For HO/U roles, render branches in a grid layout
    if (shouldShowAtmDropdown()) {
      return (
        <div key={index} className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="circle-id">{index + 1}</div>
            <span
              className="fw-medium"
              style={{
                fontSize: "14px",
                fontFamily: "Poppins",
                fontWeight: "500",
                color: "#3D475C",
              }}
            >
              {district.districtName || "District"} ({branchCount})
            </span>
          </div>

          {/* Grid layout for branches */}
          <div className="row g-3" style={{ flexWrap: "wrap" }}>
            {activeBranches.map((atm, idx) => (
              <div key={atm.id} className="col-md-6 col-lg-4">
                <div className="bg-white shadow-sm rounded-3 p-3 h-100">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="circle-sub">{idx + 1}</div>
                    <span
                      className="fw-medium small text-break"
                      style={{ fontSize: "12px", color: "#3D475C" }}
                    >
                      <strong>Atm Name:</strong> {atm.name || "NA"}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted small">
                      <strong>Atm Code:</strong> {atm.code || "NA"}
                    </span>
                  </div>
                  <div className="text-muted small">
                    <strong>Address:</strong>{" "}
                    {atm.addressLine || "No address available"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="mb-3">
        <div className="branch-card bg-white shadow-sm rounded-3 overflow-hidden">
          {/* Card Header */}
          <div
            className="branch-card-header d-flex justify-content-between align-items-center"
            onClick={() => toggleExpand(district.districtName)}
            style={{
              cursor: "pointer",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div className="circle-id">{index + 1}</div>
              <span
                className="fw-medium small"
                style={{
                  fontSize: "12px",
                  fontFamily: "Poppins",
                  fontWeight: "400",
                  color: "#3D475C",
                }}
              >
                {district.districtName || "District"}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span
                className="count-badge"
                style={{
                  backgroundColor: getPastelColor(branchCount),
                }}
              >
                {branchCount}
              </span>
              <i
                className={`bi ${
                  expandedDistrict === district.districtName
                    ? "bi-caret-up-fill"
                    : "bi-caret-down-fill"
                }`}
              ></i>
            </div>
          </div>

          {/* Expandable Sub-branch Section - Only visible when expanded */}
          {expandedDistrict === district.districtName &&
            activeBranches.length > 0 && (
              <div className="branch-card-expandable">
                <div className={shouldScroll ? "sub-branches-scroll" : ""}>
                  {activeBranches.map((branch, idx) => (
                    <div key={branch.id} className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <div className="circle-sub">{idx + 1}</div>
                        <span className="text-muted small text-break">
                          <strong>ATM Name:</strong>{" "}
                          {branch.name || "Unnamed Branch"}
                        </span>
                      </div>
                      <div
                        className="d-flex align-items-center mb-1"
                        style={{ marginLeft: "36px" }}
                      >
                        <span className="text-muted small text-break">
                          <strong>ATM CODE:</strong> {branch.code || "N/A"}
                        </span>
                      </div>

                      <div
                        className="text-muted small text-break"
                        style={{ marginLeft: "36px" }}
                      >
                        <strong>Address:</strong>{" "}
                        {branch.addressLine || "No address available"}
                      </div>
                    </div>
                  ))}
                </div>
                {shouldScroll && (
                  <div className="text-center mt-2">
                    <small className="text-muted">Scroll to see more</small>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="container py-4 bg-light"
        style={{
          borderRadius: "12px",
          maxWidth: windowWidth < 768 ? "100%" : "95%",
          margin: "0 auto",
          padding: windowWidth < 768 ? "0px" : "20px",
          boxSizing: "border-box",
        }}
      >
        <div className="row g-2 mb-4">
          {/* Custom Multi-select Districts Dropdown */}
          {shouldShowAtmDropdown() ? (
            // Branch Dropdown for HO/U roles
            <div style={{ width: "250px", marginLeft: "0px" }}>
              <div ref={dropdownRef} className="position-relative">
                <div
                  className="bg-white px-3 py-1 rounded-2 shadow-sm d-flex align-items-center justify-content-between"
                  style={{ height: "36px", cursor: "pointer" }}
                  onClick={toggleDropdown}
                >
                  <div className="w-100 d-flex justify-content-between align-items-center">
                    <span
                      style={{ fontSize: "0.8rem" }}
                      className="text-muted text-truncate"
                    >
                      {selectedAtm.length === 0
                        ? "All Atms"
                        : selectedAtm.length === 1
                        ? selectedAtm[0]
                        : `${selectedAtm.length} atms selected`}
                    </span>
                    <ChevronDown size={16} />
                  </div>
                </div>

                {dropdownOpen && (
                  <div
                    className="position-absolute w-100 mt-1 bg-white shadow-sm rounded-2 py-1 z-index-dropdown"
                    style={{
                      maxHeight: "250px",
                      overflowY: "auto",
                      zIndex: 1000,
                    }}
                  >
                    <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
                      <span style={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                        Select Atms
                      </span>
                      {selectedAtm.length > 0 && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "#1a7a73",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearAllAtmSelections();
                          }}
                        >
                          Clear All
                        </span>
                      )}
                    </div>

                    {atmData.map((atm) => (
                      <div
                        key={atm.id}
                        className="px-3 py-2 dropdown-item d-flex justify-content-between align-items-center"
                        style={{ cursor: "pointer", fontSize: "0.8rem" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAtmSelect(atm.value);
                        }}
                      >
                        <span>{atm.value}</span>
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: selectedAtm.includes(atm.value)
                              ? "#1a7a73"
                              : "white",
                          }}
                        >
                          {selectedAtm.includes(atm.value) && (
                            <Check size={12} color="white" />
                          )}
                        </div>
                      </div>
                    ))}

                    <div
                      className="border-top d-flex justify-content-end"
                      style={{ minHeight: "35px" }}
                    >
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: "0.7rem", padding: "6px" }}
                        onClick={() => {
                          setDropdownOpen(false);
                          // Force re-apply filters after a small delay to ensure state is updated
                          setTimeout(() => {
                            applyFilters();
                          }, 0);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ width: "250px", marginLeft: "0px" }}>
              <div ref={dropdownRef} className="position-relative">
                <div
                  className="bg-white px-3 py-1 rounded-2 shadow-sm d-flex align-items-center justify-content-between"
                  style={{ height: "36px", cursor: "pointer" }}
                  onClick={toggleDropdown}
                >
                  <div className="w-100 d-flex justify-content-between align-items-center">
                    <span
                      style={{ fontSize: "0.8rem" }}
                      className="text-muted text-truncate"
                    >
                      {selectedDistricts.length === 0
                        ? "All Districts"
                        : selectedDistricts.length === 1
                        ? selectedDistricts[0]
                        : `${selectedDistricts.length} districts selected`}
                    </span>
                    <ChevronDown size={16} />
                  </div>
                </div>

                {/* Custom Dropdown Menu with checkboxes */}
                {dropdownOpen && (
                  <div
                    className="position-absolute w-100 mt-1 bg-white shadow-sm rounded-2 py-1 z-index-dropdown"
                    style={{
                      maxHeight: "250px",
                      overflowY: "auto",
                      zIndex: 1000,
                    }}
                  >
                    <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
                      <span style={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                        Select Districts
                      </span>
                      {selectedDistricts.length > 0 && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "#1a7a73",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearAllSelections();
                          }}
                        >
                          Clear All
                        </span>
                      )}
                    </div>

                    {districtData.map((district) => (
                      <div
                        key={district.id}
                        className="px-3 py-2 dropdown-item d-flex justify-content-between align-items-center"
                        style={{ cursor: "pointer", fontSize: "0.8rem" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDistrictSelect(district.value);
                        }}
                      >
                        <span>{district.value}</span>
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: selectedDistricts.includes(
                              district.value
                            )
                              ? "#1a7a73"
                              : "white",
                          }}
                        >
                          {selectedDistricts.includes(district.value) && (
                            <Check size={12} color="white" />
                          )}
                        </div>
                      </div>
                    ))}

                    <div
                      className=" border-top d-flex justify-content-end"
                      style={{ minHeight: "35px" }}
                    >
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: "0.7rem", padding: "6px" }}
                        onClick={() => setDropdownOpen(false)}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Enhanced Global Search Bar */}
          <div style={{ width: "300px" }}>
            <form
              className="d-flex align-items-center bg-white rounded-2 shadow-sm"
              style={{ height: "36px" }}
            >
              <input
                type="text"
                placeholder="Search... (District, Atm, Code, Address etc.)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-grow-1"
                style={{
                  fontSize: "0.8rem",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  background: "transparent",
                  width: "100%",
                  height: "100%",
                  padding: "0 10px",
                  fontFamily: "Poppins",
                  fontWeight: "400",
                  color: "#3D475C",
                }}
              />
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                }}
              >
                <Search size={14} color="#1a7a73" />
              </div>
            </form>
          </div>
        </div>
        {/* loader*/}
        {loader && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading atms...</p>
          </div>
        )}

        {/* Branches List in Dynamic Column Layout */}
        {!loader && filteredBranches.length > 0 && (
          <div className="branch-scroll-container mt-4 bg-light rounded">
            {shouldShowAtmDropdown() ? (
              // Simple layout for HO/U roles - no column splitting
              <div>
                {filteredBranches.map((district, index) =>
                  renderDistrictCard(district, index)
                )}
              </div>
            ) : (
              <div className="row">
                {columns.map((column, colIndex) => (
                  <div
                    key={colIndex}
                    className={`col-${12 / getColumnCount()}`}
                  >
                    {column.map((district, index) =>
                      renderDistrictCard(
                        district,
                        index +
                          colIndex *
                            Math.ceil(
                              filteredBranches.length / getColumnCount()
                            )
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No results message */}
        {!loader && filteredBranches.length === 0 && (
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="bi bi-search fs-2"></i>
              <p>No matching data found for "{searchTerm}"</p>
              <small>
                Try searching for district names, atm names,codes, addresses
                etc.
              </small>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default DistrictWiseForm;
